// src/pages/Dashboard/utils/analysisProcessor.js

import { ERROR_MESSAGES, PLACEHOLDER_MESSAGES } from './dashboardConstants';

/**
 * Função auxiliar para parse seguro de JSON
 * @param {string|object} data - Dados para fazer parse
 * @returns {object|null} - Objeto parseado ou null em caso de erro
 */
const safeParse = (data) => {
  if (!data) return null;
  if (typeof data === 'object') return data;
  try {
    return JSON.parse(data);
  } catch (error) {
    console.error('Erro ao fazer parse:', error);
    return null;
  }
};

/**
 * Processa a análise jurídica do caso e extrai suas seções principais
 * @param {object} casoSelecionado - Objeto do caso selecionado
 * @returns {object} - Objeto com casoUso, estrategia e fundamentacao
 */
export const processarAnalise = (casoSelecionado) => {
  if (!casoSelecionado?.analise_caso) {
    return { 
      casoUso: ERROR_MESSAGES.ANALYSIS_NOT_AVAILABLE,
      estrategia: ERROR_MESSAGES.ANALYSIS_NOT_AVAILABLE,
      fundamentacao: ERROR_MESSAGES.ANALYSIS_NOT_AVAILABLE
    };
  }

  try {
    const analiseObj = safeParse(casoSelecionado.analise_caso);
    if (!analiseObj) {
      return { 
        casoUso: ERROR_MESSAGES.ANALYSIS_NOT_AVAILABLE,
        estrategia: ERROR_MESSAGES.ANALYSIS_NOT_AVAILABLE,
        fundamentacao: ERROR_MESSAGES.ANALYSIS_NOT_AVAILABLE
      };
    }

    const baseAnalise = analiseObj.results?.analise_juridica || analiseObj.results || analiseObj;
    
    let casoUso = '';
    let estrategia = '';
    let fundamentacao = '';
    
    // ========== PROCESSAR CASO DE USO ==========
    const casoUsoData = baseAnalise.caso_de_uso || null;
    if (casoUsoData) {
      casoUso += '📋 CASO DE USO\n\n';
      if (casoUsoData.contexto) {
        casoUso += `Contexto:\n${casoUsoData.contexto}\n\n`;
      }
      if (casoUsoData.area_direito || casoUsoData.area_do_direito) {
        casoUso += `Área do Direito: ${casoUsoData.area_direito || casoUsoData.area_do_direito}\n\n`;
      }
      if (casoUsoData.especificidade) {
        casoUso += `Especificidade:\n${casoUsoData.especificidade}\n\n`;
      }
    }
    
    // Query inicial - pode estar em vários lugares
    const queryInicial = baseAnalise.query_inicial || analiseObj.query_inicial || null;
    if (queryInicial) {
      casoUso += `📝 QUERY INICIAL\n${queryInicial}\n\n`;
    }
    
    // ========== PROCESSAR ESTRATÉGIA GERAL ==========
    const estrategiaData = baseAnalise.estrategia_geral || null;
    if (estrategiaData) {
      estrategia += '⚖️ ESTRATÉGIA GERAL\n\n';
      
      if (estrategiaData.via_principal) {
        estrategia += `Via Principal:\n${estrategiaData.via_principal}\n\n`;
      } else if (estrategiaData.plano_acao) {
        estrategia += `Plano de Ação:\n${estrategiaData.plano_acao}\n\n`;
      }
      
      if (estrategiaData.desdobramentos) {
        estrategia += `Desdobramentos:\n${estrategiaData.desdobramentos}\n\n`;
      }
      
      // Vias principais (array)
      if (Array.isArray(estrategiaData.vias_principais)) {
        estrategia += 'VIAS PRINCIPAIS:\n';
        estrategiaData.vias_principais.forEach((via, idx) => {
          estrategia += `\n${idx + 1}. ${via.via || ''}\n`;
          estrategia += `${via.descricao || ''}\n`;
        });
        estrategia += '\n';
      }
      
      // Riscos e oportunidades
      if (estrategiaData.riscos_e_oportunidades) {
        estrategia += 'RISCOS E OPORTUNIDADES:\n';
        if (estrategiaData.riscos_e_oportunidades.riscos) {
          estrategia += `\n✗ Riscos:\n${estrategiaData.riscos_e_oportunidades.riscos}\n`;
        }
        if (estrategiaData.riscos_e_oportunidades.oportunidades) {
          estrategia += `\n✓ Oportunidades:\n${estrategiaData.riscos_e_oportunidades.oportunidades}\n`;
        }
        estrategia += '\n';
      } else if (estrategiaData.riscos_oportunidades) {
        estrategia += 'RISCOS E OPORTUNIDADES:\n';
        if (estrategiaData.riscos_oportunidades.riscos) {
          estrategia += `\n✗ Riscos:\n${estrategiaData.riscos_oportunidades.riscos}\n`;
        }
        if (estrategiaData.riscos_oportunidades.oportunidades) {
          estrategia += `\n✓ Oportunidades:\n${estrategiaData.riscos_oportunidades.oportunidades}\n`;
        }
        estrategia += '\n';
      }
      
      // Alternativas estratégicas
      if (estrategiaData.alternativas_estrategicas) {
        if (typeof estrategiaData.alternativas_estrategicas === 'string') {
          estrategia += `ALTERNATIVAS ESTRATÉGICAS:\n${estrategiaData.alternativas_estrategicas}\n\n`;
        } else if (Array.isArray(estrategiaData.alternativas_estrategicas)) {
          estrategia += 'ALTERNATIVAS ESTRATÉGICAS:\n';
          estrategiaData.alternativas_estrategicas.forEach((alt, idx) => {
            estrategia += `\n${idx + 1}. ${alt.alternativa || ''}\n`;
            estrategia += `${alt.descricao || ''}\n`;
          });
          estrategia += '\n';
        }
      }
    }
    
    // ========== PROCESSAR FUNDAMENTAÇÃO LEGAL ==========
    const fundamentacaoData = baseAnalise.fundamentacao_legal || 
                             analiseObj.results?.fundamentacao_legal || 
                             analiseObj.fundamentacao_legal || null;
    
    if (fundamentacaoData) {
      fundamentacao += '📚 FUNDAMENTAÇÃO LEGAL\n\n';
      
      // Artigos principais
      if (Array.isArray(fundamentacaoData.artigos_principais)) {
        fundamentacao += '⚖️ ARTIGOS PRINCIPAIS:\n\n';
        fundamentacaoData.artigos_principais.forEach((art, idx) => {
          fundamentacao += `${idx + 1}. ${art.artigo || ''}\n`;
          if (art.codigo) fundamentacao += `   Código: ${art.codigo}\n`;
          if (art.titulo) fundamentacao += `   Título: ${art.titulo}\n`;
          if (art.relevancia) fundamentacao += `   Relevância: ${art.relevancia}\n`;
          fundamentacao += '\n';
        });
      }
      
      // Artigos expandidos
      if (Array.isArray(fundamentacaoData.artigos_expandidos)) {
        fundamentacao += '📖 ARTIGOS EXPANDIDOS:\n\n';
        fundamentacaoData.artigos_expandidos.forEach((art, idx) => {
          fundamentacao += `${idx + 1}. ${art.artigo || ''}\n`;
          if (art.codigo) fundamentacao += `   Código: ${art.codigo}\n`;
          if (art.titulo) fundamentacao += `   Título: ${art.titulo}\n`;
          if (art.relevancia) fundamentacao += `   Relevância: ${art.relevancia}\n`;
          fundamentacao += '\n';
        });
      }
      
      // Palavras-chave para jurisprudência
      if (fundamentacaoData.palavras_chaves_jurisprudencia) {
        fundamentacao += '🔍 PALAVRAS-CHAVE PARA JURISPRUDÊNCIA:\n';
        if (Array.isArray(fundamentacaoData.palavras_chaves_jurisprudencia)) {
          fundamentacaoData.palavras_chaves_jurisprudencia.forEach((palavra) => {
            fundamentacao += `  • ${palavra}\n`;
          });
        }
        fundamentacao += '\n';
      }
      
      // Se fundamentacao_legal for um array direto
      if (Array.isArray(fundamentacaoData) && fundamentacaoData.length > 0 && fundamentacaoData[0].artigo) {
        fundamentacao = '📚 FUNDAMENTAÇÃO LEGAL\n\n';
        fundamentacaoData.forEach((art, idx) => {
          fundamentacao += `${idx + 1}. ${art.artigo || 'Artigo'}\n`;
          if (art.codigo) fundamentacao += `   Código: ${art.codigo}\n`;
          if (art.titulo) fundamentacao += `   Título: ${art.titulo}\n`;
          if (art.relevancia) fundamentacao += `   Relevância: ${art.relevancia}\n`;
          fundamentacao += '\n';
        });
      }
    }
    
    // Se não encontrou dados estruturados, retornar o JSON completo para o caso de uso
    if (!casoUso && !estrategia && !fundamentacao) {
      casoUso = 'Estrutura de dados não reconhecida. Visualize o JSON completo para mais detalhes.';
    }
    
    return { 
      casoUso: casoUso || PLACEHOLDER_MESSAGES.CASE_USE,
      estrategia: estrategia || PLACEHOLDER_MESSAGES.STRATEGY,
      fundamentacao: fundamentacao || PLACEHOLDER_MESSAGES.FUNDAMENTATION
    };
    
  } catch (error) {
    console.error('Erro ao processar análise:', error);
    return { 
      casoUso: ERROR_MESSAGES.PROCESSING_ERROR,
      estrategia: ERROR_MESSAGES.PROCESSING_ERROR,
      fundamentacao: ERROR_MESSAGES.PROCESSING_ERROR
    };
  }
};

/**
 * Processa documentos sugeridos e retorna array formatado
 * @param {object} casoSelecionado - Objeto do caso selecionado
 * @returns {Array} - Array de documentos processados
 */
export const processarDocumentos = (casoSelecionado) => {
  if (!casoSelecionado?.documentos_sugeridos) return [];
  
  try {
    // Verificar se já é um array ou precisa de parse
    let docsData = casoSelecionado.documentos_sugeridos;
    
    // Se for string, fazer parse
    if (typeof docsData === 'string') {
      docsData = JSON.parse(docsData);
    }
    
    // Se for array, processar
    if (Array.isArray(docsData)) {
      return docsData.map((doc, idx) => ({
        id: idx,
        titulo: doc.titulo || `Documento ${idx + 1}`,
        justificacao: doc.justificacao || 'Documento relevante'
      }));
    }
    
    // Se for objeto mas não array, tentar converter
    if (docsData && typeof docsData === 'object') {
      return Object.values(docsData).map((doc, idx) => ({
        id: idx,
        titulo: doc.titulo || `Documento ${idx + 1}`,
        justificacao: doc.justificacao || 'Documento relevante'
      }));
    }
    
    return [];
  } catch (error) {
    console.error('Erro ao processar documentos:', error);
    return [];
  }
};